/**
 * Internal Hook: useTOSMetadataQuery
 *
 * Fetches TOS metadata (modified date) from backend using TanStack Query.
 * Used to determine if TOS needs to be re-accepted.
 */

import { useQuery } from '@tanstack/react-query';
import { createRetryFn } from '../../../config/react-query';
import { useApiManager } from '../../../hooks';
import { useUserPermissions } from '../../UserPermissionsContext';
import { SETTINGS_QUERY_KEYS, TOS_CONFIG } from '../helpers';

export const useTOSMetadataQuery = () => {
  const apiManager = useApiManager();
  const { isSuccess: isUserPermissionsReady } = useUserPermissions();
  return useQuery<{ modified: string } | null, Error>({
    queryKey: SETTINGS_QUERY_KEYS.tosMetadata,
    queryFn: async () => {
      if (!(isUserPermissionsReady && apiManager)) {
        throw new Error('API Manager not initialized');
      }
      try {
        return await apiManager.profile.fetchTosMetadata();
      } catch (error) {
        // If metadata endpoint doesn't exist, return null
        console.warn('[TOS] Metadata endpoint not available:', error);
        return null;
      }
    },
    enabled: apiManager && isUserPermissionsReady && TOS_CONFIG.autoFetch,
    retry: createRetryFn({ maxRetries: 1 }),
    staleTime: TOS_CONFIG.staleTime,
    gcTime: TOS_CONFIG.cacheTime,
    refetchOnWindowFocus: false, // Don't refetch metadata on focus
    refetchOnReconnect: false,
  });
};
